import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import axios from 'axios';

const useReferralMessages = (slug, cable, user, loggedIn) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referrerOrg, setReferrerOrg] = useState(null);
  const [partnerOrg, setPartnerOrg] = useState(null);
  const [showStartMessage, setShowStartMessage] = useState(true);
  const [hasUnread, setHasUnread] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [shareRevoked, setShareRevoked] = useState(false);
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const isActiveRef = useRef(true);
  const mountedRef = useRef(true);
  const sendErrorTimeoutRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      clearTimeout(sendErrorTimeoutRef.current);
      sendErrorTimeoutRef.current = null;
    };
  }, []);

  // Fetch message history
  useEffect(() => {
    let isMounted = true;

    if (!slug) {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/v1/referral_entities/${slug}/messages`);
        if (!isMounted) return;
        const { data } = response;
        setMessages(data.messages || []);
        setReferrerOrg(data.referrer_org);
        setPartnerOrg(data.partner_org);
        setShowStartMessage((data.messages || []).length === 0);
      } catch {
        // silently fail — messages will be empty
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMessages();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Subscribe to ActionCable channel
  useEffect(() => {
    if (!slug || !cable || !loggedIn || !user) return undefined;

    subscriptionRef.current = cable.subscriptions.create(
      {
        channel: 'ReferralMessagesChannel',
        slug,
      },
      {
        received: (response) => {
          if (response.type === 'send_error') {
            console.error('Message send failed:', response.error);
            setSendError(response.error);
            clearTimeout(sendErrorTimeoutRef.current);
            const timeoutId = setTimeout(() => {
              if (sendErrorTimeoutRef.current !== timeoutId || !mountedRef.current) return;
              sendErrorTimeoutRef.current = null;
              setSendError(null);
            }, 5000);
            sendErrorTimeoutRef.current = timeoutId;
            return;
          }
          if (response.type === 'share_revoked') {
            setShareRevoked(true);
            return;
          }
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === response.message_id)) return prev;
            return [...prev, response];
          });
          setShowStartMessage(false);
          if (!isActiveRef.current) {
            setHasUnread(true);
          }
        },
      },
    );

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [slug, cable, loggedIn, user]);

  const hasLoadedRef = useRef(false);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        if (hasLoadedRef.current) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
      if (!loading && messages.length > 0) {
        hasLoadedRef.current = true;
      }
    }
  }, [messages, loading]);

  const sendMessage = useCallback((content) => {
    if (!subscriptionRef.current || !content.trim()) return;

    subscriptionRef.current.send({
      content: content.trim(),
      sender_id: user.uid,
      sender_org: user.organization,
    });
  }, [user]);

  const markAsRead = useCallback((messageId) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform('mark_as_read', { message_id: messageId });
    }
  }, []);

  const setActive = useCallback((active) => {
    isActiveRef.current = active;
    if (active) {
      setHasUnread(false);
    }
  }, []);

  return {
    messages,
    sendMessage,
    markAsRead,
    loading,
    referrerOrg,
    partnerOrg,
    showStartMessage,
    hasUnread,
    setActive,
    messagesEndRef,
    sendError,
    shareRevoked,
  };
};

export default useReferralMessages;
